import React from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';

const GuidesPage = () => {
  const guides = [
    {
      id: 'getting-started',
      title: 'Getting Started with Patients Empowered',
      description: 'A comprehensive guide to help you start using Patients Empowered Coach and Patients Empowered Practice effectively.',
      image: '/images/guides/getting-started.jpg',
      chapters: [
        { id: 'gs-1', title: 'Creating Your Account' },
        { id: 'gs-2', title: 'Navigating the Dashboard' },
        { id: 'gs-3', title: 'Understanding the Health Form' },
        { id: 'gs-4', title: 'Using the AI Chat Feature' },
        { id: 'gs-5', title: 'Generating and Using Your Report' }
      ]
    },
    {
      id: 'preparing-doctor-visit',
      title: 'Preparing for Your Doctor Visit',
      description: 'Learn how to make the most of your healthcare appointments with proper preparation.',
      image: '/images/guides/doctor-visit.jpg',
      chapters: [
        { id: 'pdv-1', title: 'When to Start Preparing' },
        { id: 'pdv-2', title: 'Documenting Your Symptoms Effectively' },
        { id: 'pdv-3', title: 'Organizing Your Medical History' },
        { id: 'pdv-4', title: 'Preparing Questions for Your Doctor' },
        { id: 'pdv-5', title: 'What to Bring to Your Appointment' }
      ]
    },
    {
      id: 'understanding-medical-terms',
      title: 'Understanding Medical Terminology',
      description: 'A guide to help you understand common medical terms you might encounter.',
      image: '/images/guides/medical-terms.jpg',
      chapters: [
        { id: 'umt-1', title: 'Basic Anatomy Terms' },
        { id: 'umt-2', title: 'Common Diagnostic Terms' },
        { id: 'umt-3', title: 'Understanding Lab Results' },
        { id: 'umt-4', title: 'Medication Terminology' },
        { id: 'umt-5', title: 'Medical Specialties Explained' }
      ]
    },
    {
      id: 'managing-chronic-conditions',
      title: 'Managing Chronic Conditions',
      description: 'Guidance for effectively tracking and managing ongoing health conditions.',
      image: '/images/guides/chronic-conditions.jpg',
      chapters: [
        { id: 'mcc-1', title: 'Tracking Symptoms Over Time' },
        { id: 'mcc-2', title: 'Medication Management' },
        { id: 'mcc-3', title: 'Preparing for Follow-up Appointments' },
        { id: 'mcc-4', title: 'Coordinating Multiple Specialists' },
        { id: 'mcc-5', title: 'Using Patients Empowered Coach for Long-term Condition Management' }
      ]
    },
    {
      id: 'healthcare-advocacy',
      title: 'Being Your Own Healthcare Advocate',
      description: 'Learn how to effectively advocate for yourself in healthcare settings.',
      image: '/images/guides/advocacy.jpg',
      chapters: [
        { id: 'ha-1', title: 'Understanding Your Rights as a Patient' },
        { id: 'ha-2', title: 'Effective Communication with Healthcare Providers' },
        { id: 'ha-3', title: 'Getting Second Opinions' },
        { id: 'ha-4', title: 'Navigating Insurance and Billing Issues' },
        { id: 'ha-5', title: 'When and How to Seek Specialized Care' }
      ]
    },
    {
      id: 'telehealth-visits',
      title: 'Maximizing Telehealth Appointments',
      description: 'Tips and strategies for getting the most out of virtual healthcare visits.',
      image: '/images/guides/telehealth.jpg',
      chapters: [
        { id: 'tv-1', title: 'Setting Up for a Successful Telehealth Visit' },
        { id: 'tv-2', title: 'Preparing Documentation for Virtual Appointments' },
        { id: 'tv-3', title: 'Demonstrating Symptoms Virtually' },
        { id: 'tv-4', title: 'Follow-up After a Telehealth Appointment' },
        { id: 'tv-5', title: 'Using Patients Empowered with Telehealth Platforms' }
      ]
    }
  ];

  const featuredGuide = guides[0]; // Getting Started guide

  return (
    <PageTemplate title="User Guides">
      <section className="mb-5">
        <div className="card">
          <div className="grid grid-cols-1 grid-md-cols-2 gap-lg">
            <div>
              <span className="badge badge-primary mb-2">Featured Guide</span>
              <h2 className="mb-3">{featuredGuide.title}</h2>
              <p className="mb-4">{featuredGuide.description}</p>

              <h3 className="mb-3">What You'll Learn:</h3>
              <ul className="mb-4">
                {featuredGuide.chapters.map(chapter => (
                  <li key={chapter.id} className="mb-2">
                    <Link to={`/guides/${featuredGuide.id}/${chapter.id}`}>{chapter.title}</Link>
                  </li>
                ))}
              </ul>

              <Link to={`/guides/${featuredGuide.id}`} className="btn btn-primary">Start Reading</Link>
            </div>
            <div
              style={{
                height: '300px',
                backgroundColor: 'var(--color-background-alt)',
                borderRadius: 'var(--radius-md)',
                backgroundImage: `url(${featuredGuide.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
        </div>
      </section>

      <section>
        <h2 className="mb-4">All Guides</h2>
        <div className="grid grid-cols-1 grid-md-cols-2 grid-lg-cols-3 gap-lg">
          {guides.slice(1).map(guide => (
            <div key={guide.id} className="card">
              <div
                className="mb-3"
                style={{
                  height: '180px',
                  backgroundColor: 'var(--color-background-alt)',
                  borderRadius: 'var(--radius-md)',
                  backgroundImage: `url(${guide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <h3 className="mb-2">{guide.title}</h3>
              <p className="mb-3">{guide.description}</p>

              <div className="mb-3">
                <h4 className="mb-2 text-sm font-bold">Chapters:</h4>
                <ul className="text-sm">
                  {guide.chapters.slice(0, 3).map(chapter => (
                    <li key={chapter.id} className="mb-1">
                      <Link to={`/guides/${guide.id}/${chapter.id}`}>{chapter.title}</Link>
                    </li>
                  ))}
                  {guide.chapters.length > 3 && (
                    <li className="text-muted">+ {guide.chapters.length - 3} more chapters</li>
                  )}
                </ul>
              </div>

              <Link to={`/guides/${guide.id}`} className="btn btn-secondary btn-sm">View Guide</Link>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-5 text-center p-5 card">
        <h2 className="mb-3">Need More Help?</h2>
        <p className="mb-4">
          Our guides are designed to help you get the most out of Patients Empowered Coach and Patients Empowered Practice.
          If you need additional assistance, our support team is here to help.
        </p>
        <div className="flex justify-center gap-md">
          <Link to="/help" className="btn btn-primary">Visit Help Center</Link>
          <Link to="/contact" className="btn btn-secondary">Contact Support</Link>
        </div>
      </section>
    </PageTemplate>
  );
};

export default GuidesPage;
